import { TemplateIcon } from "@components/icon";
import { ISelectionChangeArgs, SelectGroups } from "@components/inputs/select/Select.types";
import { IRow, TId } from "@components/table";
import { ITabData } from "@components/tabs";
import { createBindingContext, IEntity, TEntityKey } from "@odata/BindingContext";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import { EntitySetName, IChartOfAccountsEntity } from "@odata/GeneratedEntityTypes";
import { AccountingCode, FiscalYearStatusCode, GeneralPermissionCode } from "@odata/GeneratedEnums";
import { BusinessAccountingTypes } from "@pages/companies/Company.utils";
import { isNonProfitCompany } from "@utils/CompanyUtils";
import { TFunction } from "i18next";
import React, { ReactElement } from "react";

import SmartIconSelect from "../../components/smart/smartSelect/SmartIconSelect";
import SmartHierarchyTable from "../../components/smart/smartTable/SmartHierarchyTable";
import { DASH_CHARACTER } from "../../constants";
import { AppContext } from "../../contexts/appContext/AppContext.types";
import { WithPermissionContext, withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { RowAction, Status, ToolbarItemType } from "../../enums";
import { getCorrectPath, ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES } from "../../routes";
import memoizeOneWithParamsCheck from "../../utils/memoizeOne";
import {
    TableActionOrder,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../views/table/TableToolbar.utils";
import TableView, { ITableViewBaseProps, ITableViewBaseState } from "../../views/table/TableView";
import { TableWrapper } from "../../views/table/TableView.styles";
import { getUniqName } from "../chartOfAccountsTemplates/ChartOfAccountsTemplates.utils";
import { getFiscalYearStatus } from "../fiscalYear/FiscalYearDef";
import {
    accountsIsRowWithoutAction,
    accountsRowsFactory,
    loadUnRemovableAccounts,
    rewriteChartOfAccountsWithTemplate
} from "./ChartOfAccounts.utils";
import NewTemplateDialog from "./NewTemplateDialog";

interface IState extends ITableViewBaseState {
    forceReload?: boolean;
    busy?: boolean;
    unRemovableAccounts?: number[];
    newTemplateName: string;
    showNewTemplateDialog: boolean;
}

interface IProps extends ITableViewBaseProps, WithPermissionContext {
}

enum ActionItems {
    EDIT = "edit",
    CLONE = "clone"
}

const getActionItems = memoizeOneWithParamsCheck((t: TFunction) => {
    return [{
        id: ActionItems.CLONE,
        label: t("ChartsOfAccounts:Table.Clone"),
        iconName: "Add",
        groupId: SelectGroups.Action
    }, {
        id: ActionItems.EDIT,
        label: t("ChartsOfAccounts:Table.Edit"),
        iconName: "Edit",
        groupId: SelectGroups.Action
    }];
});


class ChartOfAccountsTableView extends TableView<IProps, IState> {
    static contextType = AppContext;
    //sadly, breaks typescript type checking
    //context: React.ContextType<typeof AppContext>;

    constructor(props: IProps) {
        super(props);
        const { data, additionalData } = this.getTabData(this.props.parents);
        this.state = {
            forceReload: false,
            busy: false,
            unRemovableAccounts: null,
            newTemplateName: "",
            showNewTemplateDialog: false,
            selectedSecondaryFilter: this.getCurrentCoAKey(),
            tabSettings: {
                data,
                additionalData,
                additionalTabPrefix: props.storage.t("ChartsOfAccounts:History")
            }
        };
    }

    loadUnRemovableAccounts = memoizeOneWithParamsCheck(
        async () => {
            const unRemovableAccounts = await loadUnRemovableAccounts(this.props.storage, this.props.parentEntity?.Id);
            this.setState({ unRemovableAccounts });
        }, () => [this.props.parentEntity?.Id]);

    get accountingCode() {
        return isNonProfitCompany(this.context) ? AccountingCode.AccountingForNonprofitOrganizations : AccountingCode.AccountingForBusiness;
    }

    componentDidMount() {
        super.componentDidMount();
        this.loadUnRemovableAccounts();
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>) {
        this.loadUnRemovableAccounts();
        super.componentDidUpdate(prevProps, prevState);
    }

    handleTabChange = (id: string): void => {
        this.handleToolbarCancel();
        this.props.onParentKeyChange?.(id);
        super.handleTabChange(id);
    };

    handleTemplateChange = async (args: ISelectionChangeArgs): Promise<void> => {
        if (!args.triggerAdditionalTasks) {
            return;
        }
        switch (args.value) {
            case ActionItems.EDIT:
                this.setTemplateListUrl();
                break;
            case ActionItems.CLONE:
                const parent = this.props.parentEntity;
                const newTemplateName = await getUniqName(this.props.storage.oData, parent.Name, this.accountingCode);
                this.setState({ newTemplateName, showNewTemplateDialog: true });
                this.forceUpdate();
                break;

            default:
                // selected template from dropdown replaces all accounts of current CoA
                if (typeof args.value === "number") {
                    this.replaceAccounts(args.value);
                }
        }
    };

    setTemplateListUrl = (newTemplateId?: TEntityKey, created = false): void => {
        this.context.setViewBreadcrumbs({
            items: [],
            lockable: false
        });
        // todo: how we generally handle routes
        const pathname = getCorrectPath(`${ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES}/:ParentId`, {
            ParentId: newTemplateId?.toString()
        });
        const back = window.location.pathname;
        this.props.storage.history.push(pathname, { customData: { created, accountingCode: this.accountingCode }, back });
    };

    getCurrentCoAKey = (): string => {
        return this.props.parentEntity?.Id.toString();
    };

    getTemplatesBindingContext = memoizeOneWithParamsCheck(() => {
        return createBindingContext(EntitySetName.ChartOfAccountsTemplates, this.props.storage.oData.getMetadata());
    });

    getTemplatesSelectFieldInfo = memoizeOneWithParamsCheck((): IFieldInfo => ({
        bindingContext: this.getTemplatesBindingContext(),
        id: "",
        fieldSettings: {
            displayName: "Name",
            itemsForRender: (items) => {
                return this.canReplaceAccounts ? items : [];
            }
        },
        columns: [{ id: "Name" }]
    }), () => [this.canReplaceAccounts]);

    replaceAccounts = async (templateId: TEntityKey) => {
        this.setState({ busy: true });
        this.forceUpdate();

        const chartOfAccount = this.props.parentEntity;
        // we keep the current CoA name, there is no ui to change it during replacement

        const duplicates = await rewriteChartOfAccountsWithTemplate(chartOfAccount.Id, chartOfAccount.Name, templateId as string);

        if (duplicates.length) {
            const duplicateNames = duplicates.map((duplicate: IEntity) => `${duplicate.Account.Number} ${DASH_CHARACTER} ${duplicate.Account.Name}`).join(", ");
            this.props.storage.setTableAlert({
                status: Status.Warning,
                title: this.props.storage.t("ChartsOfAccounts:Table.DuplicateAccountsWarning", { duplicates: duplicateNames })
            });
        } else if (duplicates.error) {
            const validationMessage = duplicates.error?.validationMessages?.[0];
            this.props.storage.setTableAlert({
                status: Status.Error,
                title: this.props.storage.t("Common:Errors.ErrorHappened"),
                subTitle: validationMessage ? this.props.storage.t(`Error:${validationMessage.code}`, { currency: validationMessage.messageParameters?.currency }) : null
            });
        }

        // todo: we receive all the new data in response of the fetch, how we can pass them to smartHierarchyTable to render them directly without another fetch?
        const forceReload = !this.state.forceReload;
        this.setState({ forceReload, busy: false });
        this.forceUpdate();
    };

    getTabData = (items: IChartOfAccountsEntity[]): { data: ITabData[], additionalData: ITabData[] } => {
        const additionalData: ITabData[] = [];
        const data: ITabData[] = [];

        items.forEach((item: IEntity) => {
            const isClosed = item.FiscalYear?.StatusCode === FiscalYearStatusCode.Closed;

            const newItem = {
                id: item.Id.toString(),
                hotspotId: isClosed ? "chartOfAccountsClosedTab" : `CoATab-${item.Id.toString()}`,
                title: item.FiscalYear?.Number ?? item.Name,
                tooltip: item.FiscalYear?.StatusCode === FiscalYearStatusCode.Active ? this.props.storage.t("ChartsOfAccounts:Active") : this.props.storage.t("ChartsOfAccounts:Future"),
                status: getFiscalYearStatus(item.FiscalYear?.StatusCode)
            };

            isClosed ? additionalData.push(newItem) : data.push(newItem);
        });
        additionalData.reverse();
        return { data, additionalData };
    };

    getToolbarButtons(): TableButtonsActionType[] {
        const buttons = super.getToolbarButtons();

        if (!this.canAddOrRemoveAccount) {
            return buttons.filter(b => TableButtonsAction.Add !== b && TableButtonsAction.Remove !== b);
        }

        return buttons;
    }

    get canAddOrRemoveAccount() {
        return this.props.parentEntity.FiscalYear.StatusCode !== FiscalYearStatusCode.Closed;
    }

    get canReplaceAccounts() {
        return this.props.parentEntity.FiscalYear.StatusCode === FiscalYearStatusCode.NotUsed;
    }

    getTemplateSelectDef = (): TTableToolbarItem => {
        return {
            id: "templateSelect",
            itemType: ToolbarItemType.Custom,
            order: TableActionOrder.Remove + 1,
            render: () => {
                return (
                    <SmartIconSelect key={"templateSelect"}
                                     hotspotId={"templateSelect"}
                                     title={this.props.storage.t("ChartsOfAccounts:Table.TemplatesActionTitle")}
                                     bindingContext={this.getTemplatesBindingContext()}
                                     fieldInfo={this.getTemplatesSelectFieldInfo()}
                                     isDisabled={!!this.props.storage.data.addingRow}
                                     headerText={this.props.storage.t("ChartsOfAccounts:Table.Templates")}
                                     icon={<TemplateIcon/>}
                                     additionalItems={getActionItems(this.props.storage.t)}
                                     onChange={this.handleTemplateChange}
                                     dontDisplayNoDataFound={true} // https://solitea-cz.atlassian.net/browse/DEV-13849
                                     showSearchBoxInMenu={false} // https://solitea-cz.atlassian.net/browse/DEV-13849
                    />
                );
            }
        };
    };

    isRowWithoutAction = (rowId: TId, action: RowAction, row: IRow): boolean => {
        return accountsIsRowWithoutAction(rowId, action, row, this.state.unRemovableAccounts, this.props.storage);
    };

    rowsFactory = (rows: IRow[]): IRow[] => {
        return accountsRowsFactory(rows, this.props.storage, this.state.unRemovableAccounts, this.getRowActionType());
    };

    handleNewTemplate = (): void => {
        this.getTemplatesSelectFieldInfo().fieldSettings.items = null;
        this.handleCloseTemplateDialog();
    };

    handleCloseTemplateDialog = (): void => {
        this.setState({ showNewTemplateDialog: false });
        this.forceUpdate();
    };

    customToolbarContent = () => {
        const items: TTableToolbarItem[] = [];

        if (this.props.permissionContext.generalPermissions.has(GeneralPermissionCode.TemplateManagement)) {
            items.push(this.getTemplateSelectDef());
        }

        return items;
    };

    setAlert = (name: string): void => {
        this.props.storage.setTableAlert({
            status: Status.Success,
            title: this.props.storage.t("Common:Validation.SuccessTitle"),
            subTitle: this.props.storage.t("ChartsOfAccounts:Validation.TemplateWasCreated", { name })
        });
    };

    renderCustomDialogs(): React.ReactElement {
        const { storage, parentEntity } = this.props;
        const templateType = BusinessAccountingTypes.includes(storage.context.getCompany().AccountingCode) ? AccountingCode.AccountingForBusiness : AccountingCode.AccountingForNonprofitOrganizations;
        if (this.state.showNewTemplateDialog) {
            return <NewTemplateDialog
                newTemplateName={this.state.newTemplateName}
                templateType={templateType}
                onSave={this.handleNewTemplate}
                onClose={this.handleCloseTemplateDialog}
                parent={parentEntity}
                setAlert={this.setAlert}
            />;
        }
        return null;
    }

    renderTable(): ReactElement {
        return <TableWrapper>
            <SmartHierarchyTable  {...this.getTableSharedProps()}
                                  rowsFactory={this.rowsFactory}
                                  forceReload={this.state.forceReload}
                                  loadAll/>
        </TableWrapper>;
    }
}

export default withPermissionContext(ChartOfAccountsTableView);
